import { useEffect } from 'react'
import type { PropsWithChildren, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { CloseIcon } from './icons'

interface ModalShellProps extends PropsWithChildren {
  open: boolean
  title: string
  description?: string
  widthClassName?: string
  heightClassName?: string
  footer?: ReactNode
  onClose: () => void
}

export function ModalShell({
  open,
  title,
  description,
  widthClassName = 'max-w-3xl',
  heightClassName = 'max-h-[92vh]',
  footer,
  onClose,
  children,
}: ModalShellProps) {
  const { t } = useTranslation()

  useEffect(() => {
    if (!open) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      data-overlay-root="true"
      className="fixed inset-0 z-[1250] flex items-center justify-center bg-black/60 p-4"
    >
      <div
        className={`flex w-full flex-col overflow-hidden rounded-2xl border shadow-2xl ${heightClassName} ${widthClassName}`.trim()}
        style={{
          background: 'var(--bg-modal)',
          borderColor: 'var(--border)',
          color: 'var(--text)',
        }}
      >
        <div className="flex items-start justify-between gap-4 border-b px-5 py-4" style={{ borderColor: 'var(--border)' }}>
          <div>
            <h2 className="text-sm font-semibold">{title}</h2>
            {description ? (
              <p className="mt-1 text-[11px]" style={{ color: 'var(--text-dim)' }}>
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            title={t('common.close')}
            className="inline-flex h-6 w-6 items-center justify-center text-sm transition-colors"
            style={{ color: 'var(--text-muted)' }}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-5 py-4 text-sm">{children}</div>

        {footer ? (
          <div className="flex items-center justify-end gap-2 border-t px-5 py-4" style={{ borderColor: 'var(--border)' }}>
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  )
}
